import { Code } from 'constants/keyboard';
import { PAGE_SIZE_EDITED } from 'constants/mixpanel';
import { PageSizePreset } from 'constants/paper';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { setSizeScalable, useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { selectInfographSize } from 'modules/Editor/store/infographSelector';
import { setSize } from 'modules/Editor/store/infographSlice';
import { ChangeEvent, FocusEvent, FormEvent, KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { PageSizeUnit, PaperType } from 'types/paper.types';

import { KEEP_ORIENTATION_PAPERS, Orientation, initPageSizeMenuState } from './config';
import {
  format,
  getOrientation,
  getPageSizeExceed,
  getPaperType,
  hasExceededHDExport,
  isOrientationChanged,
  toPixels,
  validateSizeInput,
} from './helpers';

interface PageSizeControl {
  unit: PageSizeUnit;
  widthPx: number;
  heightPx: number;
  orientation: Orientation;
  isScaled?: boolean;
  paperType?: PaperType;
  isPageSizeExceeded: boolean;
  hasExceededHDExport?: boolean;
  handleOrientationChange: (orientation: Orientation) => void;
  handleChangeScaled: (e: ChangeEvent<HTMLInputElement>) => void;
  handlePaperTypeChange: (newType: PaperType, idx: number) => void;
  handleUnitChange: (e: FormEvent<HTMLSelectElement>) => void;
}

interface InputControlProps {
  unit: PageSizeUnit;
  size: number;
  type: string;
  setSize: (value: number) => void;
  setInitialSize: (initialSize: number) => void;
}

interface InputControl {
  internalValue: string;
  onChange: (value: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onBlur: (e: FocusEvent<HTMLInputElement>) => void;
}

const usePageSizeControl = (): PageSizeControl => {
  // Note: we can use react context if state control gets complex
  const pageSize = useAppSelector(selectInfographSize);
  const dispatch = useAppDispatch();

  const [state, setState] = useState(initPageSizeMenuState);

  const handleOrientationChange = useCallback(
    (orientation: Orientation) => {
      const minValue = Math.min(pageSize.widthPx, pageSize.heightPx);
      const maxValue = Math.max(pageSize.widthPx, pageSize.heightPx);

      switch (orientation) {
        case Orientation.portrait:
          dispatch(
            setSize({
              size: {
                widthPx: minValue,
                heightPx: maxValue,
              },
            }),
          );
          break;
        case Orientation.landscape:
          dispatch(
            setSize({
              size: {
                widthPx: maxValue,
                heightPx: minValue,
              },
            }),
          );
          break;
        default:
          break;
      }

      // TODO: handle magic resize on orientation change
      setState({ ...state, isScaled: false });

      Mixpanel.track(PAGE_SIZE_EDITED.eventName, {
        standard_size: pageSize.paperType,
        width: orientation === Orientation.portrait ? minValue : maxValue,
        height: orientation === Orientation.portrait ? maxValue : minValue,
        display_unit: state.unit,
        magic_resize: PAGE_SIZE_EDITED.magicResize.False,
      });
    },
    [dispatch, pageSize, state],
  );

  const handleChangeScaled = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setState({
        ...state,
        isScaled: e.target.checked,
      });
    },
    [state, setState],
  );

  const handleUnitChange = useCallback(
    (e: FormEvent<HTMLSelectElement>) => {
      const unit = (e.target as HTMLSelectElement)?.value as PageSizeUnit;
      if (unit) {
        setState({ ...state, unit });
        Mixpanel.track(PAGE_SIZE_EDITED.eventName, {
          standard_size: pageSize.paperType,
          width: pageSize.widthPx,
          height: pageSize.heightPx,
          display_unit: unit,
          magic_resize: state.isScaled ? PAGE_SIZE_EDITED.magicResize.True : PAGE_SIZE_EDITED.magicResize.False,
        });
      }
    },
    [state, pageSize],
  );

  const handlePaperTypeChange = useCallback(
    (newType: PaperType, idx: number) => {
      const value = newType as unknown as PaperType;
      // copy existing state
      const previousSize = { ...pageSize };

      if (value === pageSize.paperType || !PageSizePreset[value]) return;

      if (
        [PaperType.LETTER, PaperType.LEGAL, PaperType.TABLOID, PaperType.A3, PaperType.A4, PaperType.A5].includes(value)
      ) {
        setState({ ...state, unit: PageSizeUnit.in });
      } else {
        setState({ ...state, unit: PageSizeUnit.px });
      }

      const { widthPx, heightPx } = PageSizePreset[value];
      // Put square as portrait as existing requirement
      const isPortraitBefore = pageSize.widthPx <= pageSize.heightPx;
      const isPortraitCurrent = widthPx <= heightPx;

      const size = Object.assign(
        { paperType: value },
        isPortraitBefore === isPortraitCurrent || KEEP_ORIENTATION_PAPERS.includes(value)
          ? { widthPx, heightPx }
          : { widthPx: heightPx, heightPx: widthPx },
      );

      // TODO: implement magic resize on orientation change
      if (isOrientationChanged(size, previousSize)) {
        setState({ ...state, isScaled: false });
      }

      dispatch(setSizeScalable({ size, isKeepScale: state.isScaled }));

      Mixpanel.track(PAGE_SIZE_EDITED.eventName, {
        standard_size: value,
        width: size.widthPx,
        height: size.heightPx,
        display_unit: state.unit,
        magic_resize: state.isScaled ? PAGE_SIZE_EDITED.magicResize.True : PAGE_SIZE_EDITED.magicResize.False,
      });
    },
    [dispatch, pageSize, state],
  );

  return {
    ...state,
    ...pageSize,
    isPageSizeExceeded: getPageSizeExceed(pageSize, state.unit),
    hasExceededHDExport: hasExceededHDExport(pageSize, state.unit),
    orientation: getOrientation(pageSize),
    paperType: getPaperType(pageSize),
    handleOrientationChange,
    handleChangeScaled,
    handlePaperTypeChange,
    handleUnitChange,
  };
};

const useSizeInputControl = ({ size, unit, type, setSize, setInitialSize }: InputControlProps): InputControl => {
  const [internalValue, setInternalValue] = useState('');
  const pageSize = useAppSelector(selectInfographSize);

  const onChange = useCallback(
    (value: string) => {
      // Allow empty input field
      const updateValue = unit === PageSizeUnit.px && value !== '' ? parseInt(value).toString() : value;
      setInternalValue(updateValue);
    },
    [unit, setInternalValue],
  );

  const onBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      const value = Number(target.value);
      const formattedValue = format(size.toString(), unit);
      const newValue = toPixels(value, unit);

      setInitialSize(newValue);

      if (!validateSizeInput(value, unit)) {
        setInternalValue(formattedValue);
        return;
      }

      if (
        (type === 'height' && newValue === pageSize.heightPx) ||
        (type === 'width' && newValue === pageSize.widthPx)
      ) {
        return;
      }

      setSize?.(newValue);

      Mixpanel.track(PAGE_SIZE_EDITED.eventName, {
        standard_size: pageSize.paperType,
        width: type === 'width' ? newValue : pageSize.widthPx,
        height: type === 'height' ? newValue : pageSize.heightPx,
        display_unit: unit,
      });
    },
    [size, unit, setSize, pageSize, type, setInitialSize],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      const key = e.key || e.code;

      if (key === Code.Enter) {
        const target = e.target as HTMLInputElement;
        const value = Number(target.value);
        const formattedValue = format(size.toString(), unit);
        const newValue = toPixels(value, unit);

        setInitialSize(newValue);

        if (!validateSizeInput(value, unit)) {
          setInternalValue(formattedValue);
          return;
        }
        if (
          (type === 'height' && newValue === pageSize.heightPx) ||
          (type === 'width' && newValue === pageSize.widthPx)
        ) {
          return;
        }

        setSize?.(newValue);

        Mixpanel.track(PAGE_SIZE_EDITED.eventName, {
          standard_size: pageSize.paperType,
          width: type === 'width' ? newValue : pageSize.widthPx,
          height: type === 'height' ? newValue : pageSize.heightPx,
          display_unit: unit,
        });
      }
    },
    [size, unit, setSize, pageSize, type, setInitialSize],
  );

  useEffect(() => {
    setInternalValue(format(size, unit));
  }, [size, unit]);

  return {
    internalValue,
    onChange,
    onBlur,
    onKeyDown,
  };
};

export { usePageSizeControl, useSizeInputControl };
